/**
 * List Components
 * Ordered and unordered list components
 */

import React, { forwardRef } from 'react';
import { Box } from './Box';
import { Icon } from './Icon';

/**
 * List Component
 * Container for list items
 * 
 * @example
 * <List spacing={2}>
 *   <ListItem>Item 1</ListItem>
 *   <ListItem>Item 2</ListItem>
 * </List>
 */
export const List = forwardRef(({
  as = 'ul',
  spacing,
  styleType = 'none',
  stylePosition = 'inside',
  children,
  ...props
}, ref) => {
  return (
    <Box
      ref={ref}
      as={as}
      listStyleType={styleType}
      listStylePosition={stylePosition}
      role="list"
      css={{
        '& > li + li': spacing ? { marginTop: spacing } : undefined,
      }}
      {...props}
    >
      {children}
    </Box>
  );
});

List.displayName = 'List';

/**
 * OrderedList Component
 * Numbered list
 */
export const OrderedList = forwardRef((props, ref) => {
  return (
    <List ref={ref} as="ol" styleType="decimal" marginStart="1em" {...props} />
  );
});

OrderedList.displayName = 'OrderedList';

/**
 * UnorderedList Component
 * Bulleted list
 */
export const UnorderedList = forwardRef((props, ref) => {
  return (
    <List ref={ref} as="ul" styleType="disc" marginStart="1em" {...props} />
  );
});

UnorderedList.displayName = 'UnorderedList';

/**
 * ListItem Component
 * Individual list item
 */
export const ListItem = forwardRef((props, ref) => {
  return (
    <Box ref={ref} as="li" role="listitem" {...props} />
  );
});

ListItem.displayName = 'ListItem';

/**
 * ListIcon Component
 * Icon within a list item
 */
export const ListIcon = forwardRef((props, ref) => {
  return (
    <Icon
      ref={ref}
      role="presentation"
      marginEnd="0.5rem"
      verticalAlign="text-bottom"
      {...props}
    />
  );
});

ListIcon.displayName = 'ListIcon';

export default List;
